import React from 'react';
import {
  AlertTriangleIcon,
  Box,
  Button,
  Divider,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from '@razorpay/blade/components';
import { useIsViewEditBankAccountAllowed } from 'components/EmployeeDetails/BankAccountDetails/hooks/useIsViewEditBankAccountAllowed';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { errorParser } from 'utils/Api';

export const VerificationFailedModal = ({
  isOpen,
  onDismiss,
  onContinue,
  error,
  canRetryUpdatingBankAccount,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  onContinue: () => void;
  error: typeof AppError | null;
  canRetryUpdatingBankAccount: boolean;
}) => {
  const { isUserAdmin } = useIsViewEditBankAccountAllowed();
  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss}>
      <ModalHeader title="" />
      <ModalBody>
        <Box
          display="flex"
          flexDirection="column"
          gap="spacing.3"
          alignItems="center"
          paddingTop="spacing.6"
          paddingBottom="spacing.7">
          <AlertTriangleIcon color="feedback.icon.notice.intense" size="xlarge" />
          <Box display="flex" flexDirection="column" gap="spacing.1" alignItems="center">
            <Heading size="medium" color="surface.text.gray.normal" weight="semibold">
              Bank Verification Failed
            </Heading>
          </Box>
        </Box>
        <Box
          borderRadius="large"
          padding="spacing.5"
          display="flex"
          flexDirection="column"
          backgroundColor="surface.background.gray.moderate"
          gap="spacing.6">
          <Box>
            <Text color="surface.text.gray.muted">
              We're unable to verify the bank account details you've provided.
            </Text>
            <Text marginTop="spacing.2" color="surface.text.gray.muted">
              Reason: {errorParser(getAppErrorDetails(error))[0]}
            </Text>
          </Box>
          {!!canRetryUpdatingBankAccount && (
            <>
              <Divider width="spacing.9" />
              <Box display="flex" flexDirection="column" gap="spacing.2">
                <Heading color="surface.text.gray.normal" size="small" weight="semibold">
                  What’s the alternative?
                </Heading>
                {isUserAdmin ? (
                  <Text color="surface.text.gray.muted">
                    You can still continue to update the bank account details{' '}
                    <Text color="surface.text.gray.subtle" as="span">
                      without verification.
                    </Text>
                  </Text>
                ) : (
                  <Text color="surface.text.gray.muted">
                    Please{' '}
                    <Text color="surface.text.gray.subtle" as="span">
                      upload proof
                    </Text>{' '}
                    or edit the information if it seems incorrect.
                  </Text>
                )}
                <Text color="surface.text.gray.muted">
                  You can also{' '}
                  <Text color="surface.text.gray.subtle" as="span">
                    try again
                  </Text>{' '}
                  after 30 minutes.
                </Text>
              </Box>
            </>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.5">
          <Button variant="tertiary" onClick={onDismiss}>
            {canRetryUpdatingBankAccount ? 'Cancel' : 'Close'}
          </Button>
          {!!canRetryUpdatingBankAccount && (
            <Button variant="primary" onClick={onContinue}>
              Continue
            </Button>
          )}
        </Box>
      </ModalFooter>
    </Modal>
  );
};
